import type { FC, ReactElement } from 'react'

import type { Locale } from '@skytech/types'
import { Button } from '@skytech/ui/Button'
import {
  getDefaultCountryId,
  getFormattedPhoneNumber,
  isNil,
  isOnlyDigits,
  isOnlyLetters,
  isStringOfLength,
} from '@skytech/utils'
import { useMutation } from '@tanstack/react-query'
import { Formik, Form as FormikForm } from 'formik'

import { useAppContext } from '@/app/index.context'
import Text from '@/app/translations/Text'
import type { OnboardingForm as OnboardingFormType } from '@/app/types'
import { useSubmitFormMessage, useOnboardingForm } from '@/forms/hooks'

import UserSubmissionDate from '../UserSubmissionDate'

import { ConfirmationOptOutSubscription } from './ConfirmationOptOutSubscription'
import Identification from './Identification'
import { OnboardingType } from './OnboardingType'
import onboardingResources from './resources'
import SimCard from './SimCard'
import { SimCardType } from './SimCardType'
import SubscriptionInformation from './SubscriptionInformation'
import type { OnboardingFormFields, OnboardingPayload } from './types'
import Waiver from './Waiver'

const OperatorNote: FC = () => {
  const { data } = useOnboardingForm()

  if (!data?.operatorNote) return null

  return (
    <section>
      <h3>
        <Text id="Info fra arbeidsgiver" />
      </h3>

      <p style={{ whiteSpace: 'pre-wrap' }}>{data.operatorNote}</p>
    </section>
  )
}

const getInitValues = ({ data, locale }: { data: OnboardingFormType; locale: Locale }) => {
  const simType: 'eSim' | 'physical' | 'existing' = data.isEsim
    ? 'eSim'
    : data.simCardNo
      ? 'existing'
      : 'physical'

  return {
    isNewNumber: data.isNewNumber ? data.isNewNumber : data.isNewNumber === false ? false : '',
    isPrivateNumber: data.isPrivateNumber || '',
    isOptOutSubscription: data.status === 5 && !data.userSubmissionDate,
    comment: data.comment,
    authorizationFileName: data.authorizationFileName || '',
    authorizationFile: '',
    phoneNo: data.phoneNo ? Number(data.phoneNo) : '',
    userIsOwner: !data.birthdateOrgNo,
    birthdateOrgNo: data.birthdateOrgNo || '',
    companyName: data.nameCompany || '',
    idNumber: data.idNumber ? String(data.idNumber) : '',
    personalId: data.personalId ? String(data.personalId) : '',
    simName: data.simName,
    simAdr: data.simAdr || '',
    simZip: data.simZip || '',
    simCity: data.simCity || '',
    phoneNumberCountryId:
      data.phoneNumberCountryId || data.countryId || getDefaultCountryId(locale),
    allowPrivateNumber: data.allowPrivateNumber,
    simType,
    eid: data.eid || '',
    simCardNo: data.simCardNo || '',
  }
}

const getSubmitValues = (data: OnboardingFormFields) => ({
  is_new_number: Boolean(data.isNewNumber),
  is_private_number: Boolean(data.isPrivateNumber),
  is_opt_out_subscription: Boolean(data.isOptOutSubscription),
  comment: data.comment,
  phone_number: data.isNewNumber ? null : String(data.phoneNo),
  name_company: data.companyName || null,
  birth_date_org_no: data.birthdateOrgNo || null,
  id_number: data.idNumber || null,
  personal_id: data.personalId || null,
  sim_name: data.simName,
  sim_adr: data.simAdr,
  sim_zip: data.simZip,
  sim_city: data.simCity,
  phone_number_country_id: data.phoneNumberCountryId,
  is_esim: data.simType === 'eSim',
  sim_card_no: data.simType === 'existing' ? data.simCardNo : undefined,
  eid: data.eid,
})

const Form: FC = () => {
  const { locale } = useAppContext()
  const { SubmitFormMessagePopup, onSubmitFormSuccess, onSubmitFormError } = useSubmitFormMessage()

  const { data, refetch } = useOnboardingForm()

  if (!data) return null

  const onboardingMutation = useMutation(
    (formData: OnboardingPayload) => onboardingResources.post(formData),
    {
      onSuccess() {
        onSubmitFormSuccess()
        refetch()
      },
      onError: onSubmitFormError,
    }
  )

  return (
    <div className="onboarding">
      <Formik<OnboardingFormFields>
        validateOnBlur={false}
        initialValues={getInitValues({ data, locale })}
        validate={values => {
          const errors: Partial<Record<keyof OnboardingFormFields, string | ReactElement>> = {}
          const {
            isNewNumber,
            isPrivateNumber,
            isOptOutSubscription,
            comment,
            phoneNo,
            userIsOwner,
            companyName,
            birthdateOrgNo,
            simAdr,
            simCity,
            simName,
            simZip,
            personalId,
            authorizationFileName,
            simType,
            simCardNo,
            eid,
          } = values

          if (isOptOutSubscription) {
            if (!comment) {
              errors.comment = <Text id="This field is mandatory" />
            }

            return errors
          }

          if (isNil(isNewNumber)) {
            errors.isNewNumber = <Text id="This field is mandatory" />
          }

          if (!isNewNumber && !userIsOwner && !birthdateOrgNo) {
            errors.birthdateOrgNo = <Text id="This field is mandatory" />
          }

          if (!isNewNumber && !userIsOwner && !companyName) {
            errors.companyName = <Text id="This field is mandatory" />
          }

          if (!isNewNumber && !phoneNo) {
            errors.phoneNo = <Text id="This field is mandatory" />
          }

          if (!isNewNumber && !userIsOwner && !authorizationFileName) {
            errors.authorizationFile = <Text id="This field is mandatory" />
          }

          if (!isNewNumber && !phoneNo) {
            errors.phoneNo = <Text id="This field is mandatory" />
          }

          if (!simName) {
            errors.simName = <Text id="This field is mandatory" />
          }

          if (!simAdr && !isPrivateNumber) {
            errors.simAdr = <Text id="This field is mandatory" />
          }

          if (!simCity && !isPrivateNumber) {
            errors.simCity = <Text id="This field is mandatory" />
          }

          if (simCity && !isOnlyLetters(simCity)) {
            errors.simCity = <Text id="City name may only include letters" />
          }

          if (phoneNo && !isOnlyDigits(phoneNo)) {
            errors.phoneNo = <Text id="Phone number may only include digits" />
          }

          if (!simZip && !isPrivateNumber) {
            errors.simZip = <Text id="This field is mandatory" />
          }

          if (simZip && !isOnlyDigits(simZip)) {
            errors.simZip = <Text id="Zip code may only consist of digits" />
          }
          if (!personalId && !isPrivateNumber) {
            errors.personalId = <Text id="This field is mandatory" />
          }
          if (!isPrivateNumber && simType === 'existing' && !simCardNo) {
            errors.simCardNo = <Text id="This field is mandatory" />
          }

          if (
            !isPrivateNumber &&
            simCardNo &&
            (!isOnlyDigits(simCardNo) || !isStringOfLength(simCardNo.toString(), 20))
          ) {
            errors.simCardNo = <Text id="Sim card number must consist of 20 digits" />
          }

          if (eid && !isOnlyDigits(eid) && isStringOfLength(eid, 32)) {
            errors.eid = <Text id="Sim card number may only include digits" />
          }

          return errors
        }}
        onSubmit={formData => {
          if (data.isEditable) {
            let formattedData = { ...formData }
            if (formData.phoneNo) {
              const formattedNumber = getFormattedPhoneNumber({
                value: String(formData.phoneNo),
                countryId: Number(formData.phoneNumberCountryId),
              })
              if (formattedNumber !== formData.phoneNo) {
                formattedData.phoneNo = formattedNumber
              }
            }
            onboardingMutation.mutate(getSubmitValues(formattedData))
          }
        }}
      >
        {({ values, setFieldValue, setFieldError, setFieldTouched }) => (
          <FormikForm>
            <OnboardingType
              isEditable={data.isEditable}
              allowPrivateNumber={values.allowPrivateNumber}
              allowSubscriptions={data.allowSubscriptions}
              allowSubscriptionOptOut={data.allowSubscriptionOptOut}
              isPrivateSubscription={Boolean(data.isPrivateNumber)}
            />

            {!values.isPrivateNumber && !values.isOptOutSubscription && (
              <SimCardType
                isEditable={data.isEditable}
                simType={values.simType}
                eid={values.eid}
                simCardNo={values.simCardNo}
              />
            )}

            {values.isOptOutSubscription && (
              <ConfirmationOptOutSubscription isEditable={data.isEditable} />
            )}

            <SubscriptionInformation values={values} isEditable={data.isEditable} />

            <Waiver
              isEditable={data.isEditable}
              values={values}
              onSetFieldValues={setFieldValue}
              onSetFieldError={setFieldError}
              onSetFieldTouched={setFieldTouched}
            />

            {!values.isPrivateNumber && !values.isOptOutSubscription && (
              <Identification isEditable={data.isEditable} />
            )}

            {!values.isPrivateNumber && !values.isOptOutSubscription && (
              <SimCard isNewNumber={values.isNewNumber} isEditable={data.isEditable} />
            )}
            <OperatorNote />

            <section className="submit-section">
              <Button
                type="submit"
                disabled={!data?.isEditable}
                isLoading={onboardingMutation.isLoading}
              >
                <Text id="Send inn skjema" />
              </Button>
            </section>
          </FormikForm>
        )}
      </Formik>

      <SubmitFormMessagePopup />
    </div>
  )
}

const OnboardingForm: FC = () => (
  <>
    <UserSubmissionDate />

    <Form />
  </>
)
export default OnboardingForm
